import { CorrelationRuleModel, RoutingRuleModel, SuppressionRuleModel, useORPCClient } from "Core/oRPC";
import { MaxSearchResultCount, useUniversalSearch } from "Shared/UniversalSearch";
import { isServiceErrorDetails } from "Shared/Utils";
import { useCallback } from "react";

import { AppRegistry } from "Apps/AppRegistry";
import { HttpStatusCode } from "axios";
import { RuleType } from "../Components/RulesInfo.types";
import { universalSearchResultItem } from "Store/UniversalSearchStore";
import { useNavigate } from "react-router";

export const useRulesUniversalSearch = (ruleType?: RuleType, useTarget: boolean = true) => {
    const { icmRuleApi } = useORPCClient();

    const navigate = useNavigate();
    const onSearch = useCallback(
        async (searchText: string) => {
            if (!searchText) {
                return [];
            }
            const resultList: universalSearchResultItem[] = [];
            // Search by rule ID
            if (/^\d+$/.test(searchText)) {
                try {
                    const searchPromises = [
                        (async () => {
                            try {
                                const rule = await icmRuleApi.getGetSuppressionRuleById({ id: searchText });
                                return { type: "suppression", rule };
                            } catch {
                                return null;
                            }
                        })(),
                        (async () => {
                            try {
                                const rule = await icmRuleApi.getGetCorrelationRuleById({ id: searchText });
                                return { type: "correlation", rule };
                            } catch {
                                return null;
                            }
                        })(),
                        (async () => {
                            try {
                                const rule = await icmRuleApi.getGetRoutingRuleById({ id: searchText });
                                return { type: "routing", rule };
                            } catch {
                                return null;
                            }
                        })()
                    ];
                    const results = await Promise.all(searchPromises);
                    results.forEach((result) => {
                        if (result?.rule) {
                            const searchResult = getUniversalSearchResultItem(result.rule);
                            searchResult.title = `${result.type.replace(/^\S/, (s) => s.toUpperCase())} - ${searchResult.title}`;
                            resultList.push(searchResult);
                        }
                    });
                } catch (error) {
                    if (isServiceErrorDetails(error) && error.StatusCode !== HttpStatusCode.NotFound) {
                        throw error;
                    }
                }
            }
            return resultList.slice(0, MaxSearchResultCount).map((item) => {
                const type = item.title.split(" - ")[0] || ruleType;
                return {
                    ...item,
                    onClick: () => {
                        navigate(`/rules/${type}/view/${item.id}`);
                    },
                    navigationTarget: `/rules/${type}/view/${item.id}`
                };
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ruleType, navigate]
    );

    const { searchWord } = useUniversalSearch("Rules", useTarget ? "rules ID" : undefined, onSearch, false, AppRegistry.Rules.path);

    return { searchWord };
};

function getUniversalSearchResultItem(rule: SuppressionRuleModel | CorrelationRuleModel | RoutingRuleModel) {
    const ownerName = rule.Owner.DisplayName ?? "Unknown Service Owner";
    const ruleName = rule.Name ?? rule.Description;
    const item: universalSearchResultItem = {
        id: rule.Id?.toString() ?? "",
        title: `${rule.Id}`,
        description: `${ownerName} - ${ruleName}`
    };
    return item;
}
